import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid2,
  Paper,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import ErrorIcon from "@mui/icons-material/Error";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import dayjs from "dayjs";

import PageHeader from "../components/common/PageHeader";
import SearchFilters from "../components/common/SearchFilters";
import api from "../services/authService";
import projectService from "../services/projectService";
import { Project } from "../services/projectService";
import CommonPagination from "../components/common/CommonPagination";
import SearchInput from "../components/common/Filters/SearchInput";
import RangeDateFilter from "../components/common/Filters/RangeDateFilter";
import MultipleSelectFilter from "../components/common/Filters/MultipleSelectFilter";
import SearchButton from "../components/common/Filters/SearchButton";
import FilterGrid2 from "../components/common/Filters/FilterGrid2";
import PageContainer from "../components/common/PageContainer";
import { convertThaiDateToISO } from "../utils/dateUtils";

// ประเภทข้อมูลสำหรับ API response
interface ProjectStat {
  date: string;
  totalEmployees: number;
  checkedInEmployees: number;
  checkedOutEmployees: number;
  lateCheckins: number;
  absentEmployees: number;
}

interface ProjectData {
  projectId: string;
  projectCode: string;
  projectName: string;
  stats: ProjectStat[];
}

interface StatsResponse {
  projects: ProjectData[];
}

// ประเภทข้อมูลสำหรับแสดงผลในตาราง
interface ProjectRow {
  id: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  totalEmployees: number;
  checkedInEmployees: number;
  checkedOutEmployees: number;
  lateCheckins: number;
  absentEmployees: number;
  stats: ProjectStat[];
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // State
  const [loading, setLoading] = useState(true);
  const [projectsData, setProjectsData] = useState<ProjectData[]>([]);
  const [projectRows, setProjectRows] = useState<ProjectRow[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [totalStats, setTotalStats] = useState({
    totalEmployees: 0,
    checkedInEmployees: 0,
    checkedOutEmployees: 0,
    lateCheckins: 0,
    absentEmployees: 0,
  });
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // คำนวณข้อมูลที่จะแสดงตามหน้าและจำนวนแถวต่อหน้า
  const paginatedRows = React.useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return projectRows.slice(startIndex, endIndex);
  }, [projectRows, currentPage, rowsPerPage]);

  // โหลดข้อมูลเริ่มต้น
  useEffect(() => {
    fetchProjects();
    fetchDashboardData();
  }, []);

  // ฟังก์ชันดึงข้อมูลโครงการทั้งหมด
  const fetchProjects = async () => {
    try {
      const { projects } = await projectService.getProjects({
        page: 1,
        perPage: 100,
      });
      setAllProjects(projects);
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  // ฟังก์ชันดึงข้อมูล
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get<StatsResponse>(
        "/project-checkin-stats/combined",
        {
          params: {
            projectIds: selectedProjects.map((id) => id.toString()),
            startDate: startDate || undefined,
            endDate: endDate || undefined,
          },
        }
      );

      const { projects } = response.data;
      setProjectsData(projects);

      // แปลงข้อมูลสำหรับแสดงในตาราง
      const rows = projects.map((project) => {
        // คำนวณค่าสถิติล่าสุด (วันล่าสุดในข้อมูล)
        const sumStats = project.stats.reduce(
          (acc, curr) => {
            return {
              totalEmployees: acc.totalEmployees + curr.totalEmployees,
              checkedInEmployees:
                acc.checkedInEmployees + curr.checkedInEmployees,
              checkedOutEmployees:
                acc.checkedOutEmployees + curr.checkedOutEmployees,
            };
          },
          { totalEmployees: 0, checkedInEmployees: 0, checkedOutEmployees: 0 }
        );

        const latestStat =
          project.stats.length > 0
            ? project.stats[project.stats.length - 1]
            : {
                date: "",
                totalEmployees: 0,
                checkedInEmployees: 0,
                checkedOutEmployees: 0,
                lateCheckins: 0,
                absentEmployees: 0,
              };

        return {
          id: project.projectId,
          projectId: project.projectId,
          projectCode: project.projectCode,
          projectName: project.projectName,
          totalEmployees: latestStat.totalEmployees,
          checkedInEmployees: sumStats.checkedInEmployees,
          checkedOutEmployees: sumStats.checkedOutEmployees || 0,
          lateCheckins: 0,
          absentEmployees: 0,
          stats: project.stats,
        };
      });

      setProjectRows(rows);

      // คำนวณสถิติรวมจากทุกโครงการ
      const totals = calculateTotalStats(projects);
      setTotalStats(totals);

      setTotalItems(projects.length);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      // TODO: แสดง error message
    } finally {
      setLoading(false);
    }
  };

  // คำนวณสถิติรวมจากทุกโครงการ
  const calculateTotalStats = (projects: ProjectData[]) => {
    let totalEmployees = 0;
    let checkedInEmployees = 0;
    let checkedOutEmployees = 0;
    let lateCheckins = 0;
    let absentEmployees = 0;

    projects.forEach((project) => {
      if (project.stats.length > 0) {
        const latestStat = project.stats[project.stats.length - 1];
        totalEmployees += latestStat.totalEmployees;

        const sumStats = project.stats.reduce(
          (acc, curr) => {
            return {
              totalEmployees: acc.totalEmployees + curr.totalEmployees,
              checkedInEmployees:
                acc.checkedInEmployees + curr.checkedInEmployees,
              checkedOutEmployees:
                acc.checkedOutEmployees + curr.checkedOutEmployees,
            };
          },
          { totalEmployees: 0, checkedInEmployees: 0, checkedOutEmployees: 0 }
        );

        checkedInEmployees += sumStats.checkedInEmployees;
        checkedOutEmployees += sumStats.checkedOutEmployees;
        lateCheckins += 0;
        absentEmployees += 0;
      }
    });

    return {
      totalEmployees,
      checkedInEmployees,
      checkedOutEmployees,
      lateCheckins,
      absentEmployees,
    };
  };

  // ฟังก์ชันค้นหา
  const handleSearch = async () => {
    fetchDashboardData();
  };

  // ฟังก์ชันจัดการเลือกโครงการหลายรายการ
  const handleProjectsChange = (values: string[]) => {
    setSelectedProjects(values);
  };

  // ฟังก์ชันสำหรับขยาย/ยุบแถว
  const handleExpandRow = (projectId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // ฟังก์ชันจัดรูปแบบวันที่
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // ฟังก์ชันจัดการเปลี่ยนแปลงวันที่เริ่มต้น
  const handleRangeStartDateChange = (dateString: string) => {
    // แปลงรูปแบบวันที่จาก dd/mm/yyyy เป็น yyyy-mm-dd สำหรับ API
    setStartDate(convertThaiDateToISO(dateString));
  };

  // ฟังก์ชันจัดการเปลี่ยนแปลงวันที่สิ้นสุด
  const handleRangeEndDateChange = (dateString: string) => {
    // แปลงรูปแบบวันที่จาก dd/mm/yyyy เป็น yyyy-mm-dd สำหรับ API
    setEndDate(convertThaiDateToISO(dateString));
  };

  const stats = [
    {
      title: "พนักงานทั้งหมด",
      value: totalStats.totalEmployees || 0,
      unit: "คน",
      icon: (
        <Box
          component="img"
          src="/images/dashboard/icon-worker.png"
          sx={{
            width: { xs: 40, md: 32, lg: 40 },
            height: { xs: 40, md: 32, lg: 40 },
          }}
        />
      ),
      bgColor: "#FFFFFF",
      color: "custom.dark",
      borderColor: "#E3F2FD",
    },
    {
      title: "ผู้เข้างาน",
      value: totalStats.checkedInEmployees || 0,
      unit: "ครั้ง",
      icon: (
        <Box
          component="img"
          src="/images/dashboard/icon-checkin.png"
          sx={{
            width: { xs: 40, md: 32, lg: 40 },
            height: { xs: 40, md: 32, lg: 40 },
          }}
        />
      ),
      bgColor: "#FFFFFF",
      color: "custom.green",
      borderColor: "#E8F5E9",
    },
    {
      title: "ผู้ออกงาน",
      value: totalStats.checkedOutEmployees || 0,
      unit: "ครั้ง",
      icon: (
        <Box
          component="img"
          src="/images/dashboard/icon-absence.png"
          sx={{
            width: { xs: 40, md: 32, lg: 40 },
            height: { xs: 40, md: 32, lg: 40 },
          }}
        />
      ),
      bgColor: "#FFFFFF",
      color: "custom.red",
      borderColor: "#FFEBEE",
    },
  ];

  // เพิ่มตัวเลือก "ทั้งหมด" ในตัวเลือกโครงการ
  const projectOptions = [
    ...allProjects.map((project) => ({
      value: project.objectId,
      label: project.name,
    })),
  ];

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
  };

  return (
    <PageContainer>
      <PageHeader title="Dash Board เวลาเข้า - ออกงาน" />

      <Grid2 container spacing={3} sx={{ mb: { xs: 2, sm: 2, lg: 4 } }}>
        {stats.map((stat) => (
          <Grid2
            size={{ xs: 12, sm: 4 }}
            key={stat.title}
            sx={{ mb: { xs: 2, sm: 4 } }}
          >
            <Box
              sx={{
                p: 3,
                height: "80%",
                display: "flex",
                flexDirection: "column",
                bgcolor: stat.bgColor,
                border: `3px solid ${stat.borderColor}`,
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#666666",
                    fontSize: "1rem",
                    fontWeight: 500,
                  }}
                >
                  {stat.title}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {stat.icon}
                </Box>
                <Typography
                  variant="h3"
                  component="div"
                  sx={{
                    fontWeight: 500,
                    color: stat.color,
                    fontSize: "3.5rem",
                    mb: 1,
                    display: "flex",
                    alignItems: "baseline",
                  }}
                >
                  {stat.value}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "1rem",
                      color: "#666666",
                      ml: 1,
                    }}
                  >
                    {stat.unit}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Grid2>
        ))}
      </Grid2>

      <FilterGrid2>
        <Grid2 size={{ xs: 12, sm: 3 }}>
          <MultipleSelectFilter
            filter={{
              label: "โครงการ",
              value: "project",
              options: projectOptions,
              multiple: true,
            }}
            values={selectedProjects}
            onChange={(e, filter) => {
              const values = e.target.value as string[];
              setSelectedProjects(values);
              if (handleProjectsChange) {
                handleProjectsChange(values);
              }
            }}
          />
        </Grid2>

        <Grid2>
          <RangeDateFilter
            onStartDateChange={handleRangeStartDateChange}
            onEndDateChange={handleRangeEndDateChange}
          />
        </Grid2>
        <Grid2>
          <SearchButton onClick={handleSearch} />
        </Grid2>
      </FilterGrid2>

      <Box sx={{ width: "100%", overflow: "hidden" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">ลำดับ</TableCell>
                    <TableCell align="center">รหัสโครงการ</TableCell>
                    <TableCell>ชื่อสถานที่</TableCell>
                    <TableCell align="center">จำนวนพนักงาน</TableCell>
                    <TableCell align="center">
                      จำนวนผู้เข้างาน (ครั้ง)
                    </TableCell>
                    <TableCell align="center">จำนวนผู้ออกงาน (ครั้ง)</TableCell>
                    <TableCell align="center">วันที่</TableCell>
                    <TableCell align="center">ดูรายละเอียด</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectRows.length > 0 ? (
                    paginatedRows.map((row, index) => (
                      <React.Fragment key={row.projectId}>
                        <TableRow>
                          <TableCell align="center">{(currentPage - 1) * rowsPerPage + index + 1}</TableCell>
                          <TableCell align="center">
                            {row.projectCode}
                          </TableCell>
                          <TableCell>{row.projectName}</TableCell>
                          <TableCell
                            align="center"
                            sx={{ color: "primary.main", fontWeight: 500 }}
                          >
                            {row.totalEmployees}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ color: "custom.green", fontWeight: 500 }}
                          >
                            {row.checkedInEmployees}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ color: "custom.red", fontWeight: 500 }}
                          >
                            {row.checkedOutEmployees || 0}
                          </TableCell>
                          <TableCell align="center">
                            {formatDate(
                              row.stats[row.stats.length - 1]?.date || ""
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleExpandRow(row.projectId)}
                              size="small"
                              sx={{
                                color: "primary.main",
                                "&:hover": { color: "primary.dark" },
                              }}
                            >
                              {expandedRows[row.projectId] ? (
                                <RemoveIcon />
                              ) : (
                                <AddIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={8}
                          >
                            <Collapse
                              in={expandedRows[row.projectId] || false}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ margin: 1 }}>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  component="div"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  ประวัติการเข้างาน - {row.projectName}
                                </Typography>
                                <Table
                                  size="small"
                                  aria-label="project stats history"
                                  sx={{
                                    "& .MuiTableCell-root": {
                                      border: "none", // or borderBottom: 'none'
                                    },
                                  }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell align="center">
                                        วันที่
                                      </TableCell>
                                      <TableCell align="center">
                                        จำนวนพนักงาน
                                      </TableCell>
                                      <TableCell align="center">
                                        จำนวนผู้เข้างาน (ครั้ง)
                                      </TableCell>
                                      <TableCell align="center">
                                        จำนวนผู้ออกงาน (ครั้ง)
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.stats.length > 0 ? (
                                      row.stats.map((stat) => (
                                        <TableRow key={stat.date}>
                                          <TableCell align="center">
                                            {formatDate(stat.date)}
                                          </TableCell>
                                          <TableCell
                                            align="center"
                                            sx={{ color: "primary.main" }}
                                          >
                                            {stat.totalEmployees}
                                          </TableCell>
                                          <TableCell
                                            align="center"
                                            sx={{ color: "custom.green" }}
                                          >
                                            {stat.checkedInEmployees}
                                          </TableCell>
                                          <TableCell
                                            align="center"
                                            sx={{ color: "custom.red" }}
                                          >
                                            {stat.checkedOutEmployees || 0}
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    ) : (
                                      <TableRow>
                                        <TableCell
                                          colSpan={5}
                                          align="center"
                                          sx={{ py: 3 }}
                                        >
                                          <Typography
                                            variant="body1"
                                            color="text.secondary"
                                          >
                                            ไม่พบข้อมูลประวัติการเข้างาน
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Typography variant="h6" color="text.secondary">
                          ไม่พบข้อมูลโครงการ
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          กรุณาเลือกโครงการและช่วงวันที่ที่ต้องการดูข้อมูล
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        <CommonPagination
          count={Math.ceil(totalItems / rowsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalItems={totalItems}
        />
      </Box>
    </PageContainer>
  );
};

export default Dashboard;
